/**
 * 把整包素材拖進景點——上傳與歸位一次做完。
 *
 * ## 為什麼原本的「從資料夾套用」不夠
 *
 * 那個功能能一次填滿三語系的所有欄位，但前提是**媒體庫裡已經存在**
 * `{roi}/{zh,en,jp}` 這樣的標籤結構。也就是說，使用者得先自己到媒體庫、把資料夾
 * 拖上去、還要正好命名成景點的窗屏編號，套用才會掃得到。會這樣整理檔案的是廠商，
 * 不是每天在用 CMS 的人——於是這個唯一的自動化路徑實際上沒人走得到。
 *
 * 這裡把前置條件消掉：拖進來的資料夾**叫什麼名字都可以**，上傳時以語系段為錨點
 * 改寫標籤（見 {@link retargetFolderTag}），傳完立刻自動跑一次計畫預覽。使用者
 * 從頭到尾只做一個動作。
 *
 * ## 不猜語系
 *
 * 路徑裡找不到 zh／en／jp 的檔案一律不上傳，並列在畫面上。猜一個語系塞進去，
 * 錯的下場是船上某個語系播到別的語言，而且沒有任何跡象——寧可講出來讓人自己補。
 */

import { useRef, useState } from 'react';

import { Button } from './Button';
import { uploadMediaWithProgress } from '../lib/api-client';
import {
  languageFromPath,
  pickedFromDataTransfer,
  pickedFromFileList,
  retargetFolderTag,
  validateFile,
  type PickedFile,
} from '../lib/media-upload';

interface Props {
  /** 目標資料夾（通常是景點的窗屏編號）；空字串時停用。 */
  folder: string;
  /** 全部上傳完成後呼叫，供呼叫端自動跑計畫預覽。 */
  onUploaded: () => void;
}

interface Progress {
  total: number;
  done: number;
  /** 目前正在傳的檔名，讓長時間的影片上傳看得出還活著。 */
  current: string;
}

interface SkippedFile {
  path: string;
  reason: string;
}

export function SpotFolderUpload({ folder, onUploaded }: Props): JSX.Element {
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [skipped, setSkipped] = useState<SkippedFile[]>([]);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dirInput = useRef<HTMLInputElement>(null);

  const target = folder.trim();

  const run = async (picked: PickedFile[]): Promise<void> => {
    setDone(null);
    setError(null);
    const rejected: SkippedFile[] = [];
    const queue: Array<{ file: File; tag: string; language: string }> = [];

    for (const p of picked) {
      const invalid = validateFile(p.file);
      if (invalid) {
        rejected.push({ path: p.path, reason: invalid });
        continue;
      }
      const tag = retargetFolderTag(p.path, target);
      const language = languageFromPath(p.path);
      if (tag === null || language === null) {
        rejected.push({
          path: p.path,
          reason: '路徑裡沒有 zh／en／jp 資料夾，無法判斷語系',
        });
        continue;
      }
      queue.push({ file: p.file, tag, language });
    }

    setSkipped(rejected);
    if (queue.length === 0) {
      setProgress(null);
      if (rejected.length > 0) setError('這一批沒有任何檔案可以上傳，詳見下方清單。');
      return;
    }

    setProgress({ total: queue.length, done: 0, current: queue[0]?.file.name ?? '' });
    let failed = 0;
    for (const [i, item] of queue.entries()) {
      setProgress({ total: queue.length, done: i, current: item.file.name });
      try {
        await uploadMediaWithProgress(item.file, { language: item.language, tags: [item.tag] });
      } catch (e) {
        // 單一檔案失敗不中斷整批：一包 300 個檔傳到第 200 個壞掉時，
        // 中斷等於前面 199 個也要重來
        failed += 1;
        rejected.push({ path: item.file.name, reason: (e as Error).message });
      }
    }

    setSkipped([...rejected]);
    setProgress(null);
    setDone(`已上傳 ${queue.length - failed} 個檔案到 ${target}／…；下方已自動產生套用計畫。`);
    if (failed > 0) setError(`${failed} 個檔案上傳失敗，詳見下方清單。`);
    onUploaded();
  };

  const handle = (picked: PickedFile[]): void => {
    if (target === '') {
      setError('請先填上方的資料夾（通常是景點的窗屏編號），再拖檔案進來。');
      return;
    }
    void run(picked);
  };

  const busy = progress !== null;

  return (
    <div style={{ margin: '8px 0' }}>
      <input
        ref={dirInput}
        type="file"
        multiple
        // React 未內建這兩個屬性的型別；資料夾選取只能靠它們
        {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files) handle(pickedFromFileList(e.target.files));
          e.target.value = '';
        }}
      />

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (busy) return;
          void pickedFromDataTransfer(e.dataTransfer).then(handle);
        }}
        style={{
          border: `2px dashed ${dragging ? '#2563eb' : '#c7d2fe'}`,
          background: dragging ? '#eff6ff' : '#fff',
          borderRadius: 6,
          padding: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <Button variant="secondary" onClick={() => dirInput.current?.click()} disabled={busy}>
          📁 選擇整個資料夾
        </Button>
        <span style={{ fontSize: 12, color: '#6b7280', flex: 1, minWidth: 240 }}>
          或把整包素材拖進來。資料夾<strong>叫什麼名字都可以</strong>，只要裡面有 <code>zh</code> /{' '}
          <code>en</code> / <code>jp</code> 這層；上傳後會自動歸到 <code>{target || 'N'}/…</code>{' '}
          並產生套用計畫。
        </span>
      </div>

      {progress && (
        <div style={{ marginTop: 6, fontSize: 12 }}>
          <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
            <div
              style={{
                width: `${Math.round((progress.done / progress.total) * 100)}%`,
                height: '100%',
                background: '#2563eb',
                transition: 'width 120ms linear',
              }}
            />
          </div>
          <div style={{ color: '#6b7280', marginTop: 3 }}>
            上傳中 {progress.done}／{progress.total}：{progress.current}
          </div>
        </div>
      )}

      {done && (
        <div
          style={{
            marginTop: 6,
            padding: 8,
            fontSize: 12,
            background: '#ecfdf5',
            border: '1px solid #6ee7b7',
            borderRadius: 4,
          }}
        >
          ✓ {done}
        </div>
      )}
      {error && (
        <div className="error-banner" style={{ marginTop: 6 }}>
          {error}
        </div>
      )}

      {skipped.length > 0 && (
        <details style={{ marginTop: 6, fontSize: 12 }}>
          <summary style={{ cursor: 'pointer', color: '#b45309' }}>
            {skipped.length} 個檔案未上傳
          </summary>
          <div style={{ marginTop: 4, color: '#6b7280' }}>
            {skipped.map((s) => (
              <div key={`${s.path}|${s.reason}`} style={{ padding: '2px 0' }}>
                <code style={{ wordBreak: 'break-all' }}>{s.path}</code> — {s.reason}
              </div>
            ))}
            <div style={{ marginTop: 4 }}>
              這些檔仍可在對應欄位的「＋ 選擇素材」裡就地上傳——那條路徑由插槽自己決定語系，
              不需要語系資料夾。
            </div>
          </div>
        </details>
      )}
    </div>
  );
}
